/**
 * In-memory wizard state for the subscription flow.
 *
 * Holds the user's chosen offering selection — caraSku, frequency,
 * canonical unit price, label, offering name — populated by the
 * landing page after it fetched the offering from the API. The
 * checkout adapter reads from here so it never has to refetch.
 *
 * State is held in React context, not the URL. A page refresh on
 * /checkout will leave `selection` null — the checkout adapter detects
 * that and bounces the user back to the landing page where they can
 * pick again.
 */

import React, { createContext, ReactNode, useContext, useState } from "react";

export type SubscriptionSelection = {
  /** Display name of the offering — used as the cart-line name. */
  offeringName: string;
  /** Canonical CARA SKU, sent to the BE on create. */
  caraSku: string;
  /** Pricing-option frequency, e.g. "weekly". */
  frequency: string;
  /** Pricing-option unit price (dollars, for cart math + display). */
  unitPrice: number;
  /** Pricing-option label, e.g. "Weekly". Used as the cart-line description. */
  label: string;
};

export type SubscriptionCheckoutState = {
  selection: SubscriptionSelection | null;
};

type SubscriptionCheckoutContextType = {
  state: SubscriptionCheckoutState;
  setSelection: (selection: SubscriptionSelection) => void;
  reset: () => void;
};

const initial: SubscriptionCheckoutState = { selection: null };

const SubscriptionCheckoutContext = createContext<
  SubscriptionCheckoutContextType | undefined
>(undefined);

export function useSubscriptionCheckout(): SubscriptionCheckoutContextType {
  const ctx = useContext(SubscriptionCheckoutContext);
  if (!ctx) {
    throw new Error(
      "useSubscriptionCheckout must be used within a SubscriptionCheckoutProvider",
    );
  }
  return ctx;
}

export function SubscriptionCheckoutProvider({
  children,
  initialSelection = null,
}: {
  children: ReactNode;
  // Test-only escape hatch. Production code never passes this.
  initialSelection?: SubscriptionSelection | null;
}) {
  const [state, setState] = useState<SubscriptionCheckoutState>(
    initialSelection !== null ? { selection: initialSelection } : initial,
  );

  const setSelection = (selection: SubscriptionSelection) =>
    setState({ selection });
  const reset = () => setState(initial);

  return (
    <SubscriptionCheckoutContext.Provider
      value={{ state, setSelection, reset }}
    >
      {children}
    </SubscriptionCheckoutContext.Provider>
  );
}
